// 🗂️ B329 (AI_148) — REGISTRO CANÔNICO + GOVERNANÇA TEMPORAL DO CORPUS RAG
// ─────────────────────────────────────────────────────────────────────────
// Registra, de forma VERSIONADA e revisável, a situação temporal de cada
// documento do corpus: se está VIGENTE, foi SUBSTITUÍDO por uma versão mais
// nova, está em VACATIO LEGIS (norma publicada mas ainda não vigente — não pode
// ser nomeada) ou é HISTÓRICO. Serve a dois fins:
//   (1) registro canônico (governança/auditoria — não existia em lugar nenhum);
//   (2) desempate do ranking do RAG: um doc SUBSTITUÍDO não vence o VIGENTE em
//       empate de relevância (sortByRelevance, buscarContexto.ts). NÃO filtra —
//       o doc antigo segue recuperável; só perde o empate.
//
// Enxuto: só lista as EXCEÇÕES; todo doc não-listado é `vigente` por padrão.
// Chave = `source` (nome do arquivo no Pinecone, mesmos nomes do B331).
// A restrição de citação da 8.041 já é APLICADA nos prompts (B316/B219/analyze.js);
// aqui ela é só DOCUMENTADA canonicamente — este módulo NÃO altera prompt.
// Curadoria das supersessões: docs/B329-governanca-corpus.md (revisão humana).

export type StatusVigencia = "vigente" | "substituido" | "vacatio" | "historico";

export interface GovernancaDoc {
  status: StatusVigencia;
  /** `source` do documento VIGENTE que substitui este (quando `substituido`). */
  substituidoPor?: string;
  /** Restrição de nomeação em texto gerado (ex.: vacatio legis). Documental. */
  restricaoCitacao?: string;
  /** Justificativa curta da classificação. */
  motivo?: string;
  /** Data da última revisão HUMANA desta entrada (AAAA-MM-DD). */
  revisadoEm?: string;
}

const VIGENTE: GovernancaDoc = { status: "vigente" };

// Só as EXCEÇÕES. Seed conservador (só o inequívoco).
// ⚖️ Candidatos de supersessão AVALIADOS por Leonardo (AI_148, 15/07/2026) e mantidos
// VIGENTES — os três COMPLEMENTAM, não substituem (não entram no registro):
//   • HCC AASLD "critical update" × guidance-base → só complementa.
//   • NAFLD × atualização de nomenclatura MASLD → só complementa.
//   • IMbrave050 original (2023) × "updated data" (2026) → convivem.
// Ver docs/B329-governanca-corpus.md.
const REGISTRO_BRUTO: Record<string, GovernancaDoc> = {
  "PORTARIA GM_MS N 8.041 DE 25 DE SETEMBRO DE 2025 - republicação em 21-01-2026 (1).pdf":
    {
      status: "vacatio",
      restricaoCitacao:
        "Não nomear a Portaria 8.041 como norma vigente (vacatio legis) — usar o CONTEÚDO, não o nome (B316).",
      motivo:
        "Norma publicada/republicada mas ainda em vacatio; deve ser recuperada e usada, sem citar o nome.",
      revisadoEm: "2026-07-15",
    },
  "primary_biliary_cholangitis__2018_practice.32.pdf": {
    status: "substituido",
    substituidoPor: "primary_biliary_cholangitis__2021_practice.24.pdf",
    motivo:
      "AASLD PBC 2021 Practice Guidance Update substitui a de 2018 (mesma sociedade/série).",
    revisadoEm: "2026-07-15",
  },
};

// macOS grava nomes de arquivo com acento em NFD; o `source` que chega do Pinecone
// pode vir em NFD e a chave digitada aqui em NFC → normalizar ambos para casar.
const REGISTRO: Record<string, GovernancaDoc> = Object.fromEntries(
  Object.entries(REGISTRO_BRUTO).map(([k, v]) => [k.normalize("NFC"), v])
);

/** Governança de um documento pelo seu `source`. Não-listado → `vigente`. */
export function governancaDoc(source?: string): GovernancaDoc {
  if (!source) return VIGENTE;
  return REGISTRO[source.normalize("NFC")] || VIGENTE;
}

/**
 * Penalidade de vigência para o DESEMPATE do ranking (B329): `substituido`/
 * `historico` (0→1) perdem o empate para o vigente. `vacatio` NÃO é penalizado
 * (norma-em-espera; deve ser recuperada — B316). Só desempata; jamais supera
 * diferença real de score (a trava |scoreDiff|>0,05 continua mandando).
 */
export function penalidadeVigencia(source?: string): 0 | 1 {
  const s = governancaDoc(source).status;
  return s === "substituido" || s === "historico" ? 1 : 0;
}
